import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, UserPlus01, Copy01, X, Mail01 } from "@untitledui/icons";
import { useListData } from "react-stately";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { MultiSelect } from "@/components/base/select/multi-select";
import { TextArea } from "@/components/base/textarea/textarea";
import { Input } from "@/components/base/input/input";
import { Badge } from "@/components/base/badges/badges";
import { RadioGroup, RadioButton } from "@/components/base/radio-buttons/radio-buttons";
import { Avatar } from "@/components/base/avatar/avatar";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { documentService } from "@/data/mock-documents";
import { documentInviteService } from "@/data/mock-document-invites";
import { accountService } from "@/data/mock-accounts";
import { addressBookService } from "@/data/mock-address-book";
import { getCurrentUserType } from "@/components/application/command-menu/command-menu-search";
import { useClipboard } from "@/hooks/use-clipboard";
import { useDocumentPolygons } from "@/hooks/use-document-polygons";
import { PdfViewer } from "@/components/application/pdf-viewer/pdf-viewer";
import { PolygonManager } from "@/components/application/pdf-viewer/polygon-manager";
import { AnnotationDrawer } from "@/components/application/pdf-viewer/annotation-drawer";
import type { ReviewPolygon } from "@/types/document-review";
import type { SelectItemType } from "@/components/base/select/select";
import type { DocumentInvite } from "@/types/document-invite";
// PDF di test ora servito dalla cartella public
const testPdfUrl = "/document-review-test.pdf";
import { useTranslation } from "react-i18next";

export default function ShipperDocumentReviewPage() {
    const { t } = useTranslation(['pages:documents', 'common']);
    const { containerId, documentId } = useParams<{ containerId: string; documentId: string }>();
    const { polygons, selectedPolygon, createPolygon, updatePolygon, deletePolygon, selectPolygon, setSelectedPolygon } = useDocumentPolygons(documentId);
    const [currentPage, setCurrentPage] = useState(1);

    // Wrapper per selezionare poligono e cambiare pagina
    const handleSelectPolygon = useCallback((polygonId: string) => {
        const polygon = polygons.find(p => p.id === polygonId);
        if (polygon) {
            // Prima seleziona il poligono (per assicurarsi che sia nello stato)
            selectPolygon(polygonId);
            // Poi cambia pagina alla pagina del poligono
            // Usa requestAnimationFrame per assicurarsi che la selezione avvenga prima del cambio pagina
            requestAnimationFrame(() => {
                setCurrentPage(polygon.pageNumber);
                // Seleziona nuovamente dopo il cambio pagina per assicurarsi che il poligono sia selezionato sulla nuova pagina
                setTimeout(() => {
                    selectPolygon(polygonId);
                }, 100);
            });
        }
    }, [polygons, selectPolygon]);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteNotes, setInviteNotes] = useState("");
    const [inviteType, setInviteType] = useState<"receiver" | "email" | "addressbook">("receiver");
    const [customEmail, setCustomEmail] = useState("");
    const [shareLink, setShareLink] = useState<string | null>(null);
    const [existingInvites, setExistingInvites] = useState<DocumentInvite[]>([]);
    const [linkExpiration, setLinkExpiration] = useState("never");
    const [linkPermissions, setLinkPermissions] = useState({ canView: true, canEdit: false });

    const { copy, copied } = useClipboard();

    const doc = useMemo(() => (documentId ? documentService.getById(documentId) : undefined), [documentId]);
    const isShipper = getCurrentUserType() === "SHIPPER";

    // Get receiver accounts for MultiSelect
    const receiverAccounts = useMemo(() => {
        return accountService.getAll().filter((acc) => acc.userType === "RECEIVER");
    }, []);

    // Convert receiver accounts to SelectItemType
    const receiverItems: SelectItemType[] = useMemo(() => {
        return receiverAccounts.map((acc) => ({
            id: acc.id,
            label: acc.legalName,
            supportingText: acc.email,
        }));
    }, [receiverAccounts]);

    // Selected receivers state
    const selectedReceivers = useListData<SelectItemType>({
        initialItems: [],
    });

    // Get address book contacts for current shipper
    const addressBookContacts = useMemo(() => {
        const accountId = localStorage.getItem("accountId") || "";
        if (!accountId) return [];
        return addressBookService.getByAccountId(accountId);
    }, []);

    // Convert address book contacts to SelectItemType
    const addressBookItems: SelectItemType[] = useMemo(() => {
        return addressBookContacts.map((contact) => ({
            id: contact.id,
            label: `${contact.firstName} ${contact.lastName}`,
            supportingText: `${contact.email} • ${contact.role}`,
        }));
    }, [addressBookContacts]);

    // Selected address book contacts state
    const selectedAddressBookContacts = useListData<SelectItemType>({
        initialItems: [],
    });

    // Load existing invites when modal opens
    useEffect(() => {
        if (isInviteModalOpen && documentId) {
            const invites = documentInviteService.getByDocumentId(documentId);
            setExistingInvites(invites);
        }
    }, [isInviteModalOpen, documentId]);

    // Handle creating a new polygon
    const handleCreatePolygon = useCallback((options?: { pageNumber?: number; x?: number; y?: number }) => {
        if (!documentId) return;

        const pageNumber = options?.pageNumber ?? currentPage;
        const x = options?.x ?? 0.416;
        const y = options?.y ?? 0.464;

        createPolygon({
            pageNumber,
            x,
            y,
            width: 0.168,
            height: 0.071,
            notes: "",
            attributes: {},
            comments: [],
        });
    }, [documentId, currentPage, createPolygon]);

    // Handle updating a polygon
    const handleUpdatePolygon = useCallback((updates: Partial<ReviewPolygon>) => {
        if (!selectedPolygon) return;
        updatePolygon(selectedPolygon.id, updates);
    }, [selectedPolygon, updatePolygon]);

    const handleSendInvites = () => {
        if (!documentId) return;

        const shipperId = localStorage.getItem("accountId") || "";
        if (!shipperId) return;

        if (inviteType === "receiver") {
            if (selectedReceivers.items.length === 0) return;

            // Create invites for each selected receiver
            selectedReceivers.items.forEach((receiver) => {
                documentInviteService.create({
                    documentId,
                    shipperId,
                    type: "receiver",
                    receiverId: receiver.id,
                    notes: inviteNotes.trim() || undefined,
                });
            });
        } else if (inviteType === "email") {
            if (!customEmail.trim()) return;

            // Create invite for custom email
            documentInviteService.create({
                documentId,
                shipperId,
                type: "email",
                email: customEmail.trim(),
                notes: inviteNotes.trim() || undefined,
            });
        } else if (inviteType === "addressbook") {
            if (selectedAddressBookContacts.items.length === 0) return;

            // Create invites for each selected address book contact
            selectedAddressBookContacts.items.forEach((contact) => {
                const addressBookEntry = addressBookContacts.find((c) => c.id === contact.id);
                if (addressBookEntry) {
                    documentInviteService.create({
                        documentId,
                        shipperId,
                        type: "addressbook",
                        email: addressBookEntry.email,
                        addressBookEntryId: addressBookEntry.id,
                        notes: inviteNotes.trim() || undefined,
                    });
                }
            });
        }

        // Reload invites and reset form
        const invites = documentInviteService.getByDocumentId(documentId);
        setExistingInvites(invites);
        selectedReceivers.setSelectedKeys(new Set());
        // Clear selected receivers by removing all items
        selectedReceivers.items.forEach((item) => {
            selectedReceivers.remove(item.id);
        });
        selectedAddressBookContacts.setSelectedKeys(new Set());
        // Clear selected address book contacts by removing all items
        selectedAddressBookContacts.items.forEach((item) => {
            selectedAddressBookContacts.remove(item.id);
        });
        setCustomEmail("");
        setInviteNotes("");
    };

    const handleRevokeInvite = (inviteId: string) => {
        documentInviteService.revokeInvite(inviteId);
        if (documentId) {
            const invites = documentInviteService.getByDocumentId(documentId);
            setExistingInvites(invites);
        }
    };

    const handleResendInvite = (inviteId: string) => {
        documentInviteService.resendInvite(inviteId);
        if (documentId) {
            const invites = documentInviteService.getByDocumentId(documentId);
            setExistingInvites(invites);
        }
    };

    const handleGenerateLink = () => {
        if (!documentId) return;
        const link = documentInviteService.generateShareLink(documentId);
        setShareLink(link);
    };

    const handleCopyLink = () => {
        if (shareLink) {
            copy(shareLink);
        }
    };

    const getInviteDisplayName = (invite: DocumentInvite): string => {
        if (invite.type === "email" && invite.email) {
            return invite.email;
        }
        if (invite.type === "addressbook" && invite.addressBookEntryId) {
            const entry = addressBookService.getById(invite.addressBookEntryId);
            if (entry) {
                return `${entry.firstName} ${entry.lastName}`;
            }
            return invite.email || "Sconosciuto";
        }
        if (invite.type === "receiver" && invite.receiverId) {
            const account = accountService.getById(invite.receiverId);
            return account?.legalName || invite.receiverId;
        }
        return "Sconosciuto";
    };

    const getInviteDisplayEmail = (invite: DocumentInvite): string | undefined => {
        if (invite.type === "email" || invite.type === "addressbook") {
            return invite.email;
        }
        if (invite.type === "receiver" && invite.receiverId) {
            const account = accountService.getById(invite.receiverId);
            return account?.email;
        }
        return undefined;
    };

    const getStatusBadgeColor = (status: DocumentInvite["status"]): "warning" | "success" | "error" => {
        switch (status) {
            case "pending":
                return "warning";
            case "accepted":
                return "success";
            case "rejected":
                return "error";
            default:
                return "warning";
        }
    };

    const getStatusLabel = (status: DocumentInvite["status"]): string => {
        switch (status) {
            case "pending":
                return t('review.status.pending');
            case "accepted":
                return t('review.status.accepted');
            case "rejected":
                return t('review.status.rejected');
            default:
                return status;
        }
    };

    const fileUrl = testPdfUrl as string;

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4 lg:pr-[380px]">
                <div className="flex items-center gap-3">
                    <Link to={`/shipper/containers/${containerId}?tab=documents`} className="text-tertiary hover:text-primary">
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-xl font-semibold text-primary">{t('review.title')}</h1>
                        <div className="text-sm text-tertiary">{doc?.name || t('review.loading')}</div>
                    </div>
                </div>
                {isShipper && (
                    <Button iconLeading={UserPlus01} onClick={() => setIsInviteModalOpen(true)}>
                        {t('review.share.title')}
                    </Button>
                )}
            </div>
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-0">
                <div className="relative">
                    <PdfViewer
                        fileUrl={fileUrl}
                        polygons={polygons}
                        onCreatePolygon={handleCreatePolygon}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        renderOverlay={({ pageNumber, pageWidth, pageHeight }) => (
                            <PolygonManager
                                pageNumber={pageNumber}
                                pageWidth={pageWidth}
                                pageHeight={pageHeight}
                                polygons={polygons}
                                selectedPolygonId={selectedPolygon?.id || null}
                                onChange={updatePolygon}
                                onSelect={selectPolygon}
                                onDelete={deletePolygon}
                            />
                        )}
                    />
                </div>
                <AnnotationDrawer
                    isOpen={true}
                    polygon={selectedPolygon}
                    allPolygons={polygons}
                    documentId={documentId || "1"}
                    onClose={() => setSelectedPolygon(null)}
                    onUpdate={handleUpdatePolygon}
                    onSelectPolygon={handleSelectPolygon}
                    onDelete={deletePolygon}
                    onMarkResolved={(id, resolved) => {
                        const polygon = polygons.find(p => p.id === id);
                        if (!polygon) return;
                        updatePolygon(id, {
                            attributes: { ...polygon.attributes, resolved: String(resolved) }
                        });
                    }}
                />
            </div>

            {/* Modal di invito */}
            {isInviteModalOpen && (
                <ModalOverlay isOpen={isInviteModalOpen} onOpenChange={setIsInviteModalOpen} isDismissable>
                    <Modal className="max-w-2xl">
                        <Dialog className="flex flex-col rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-semibold text-primary">{t('review.share.title')}</h2>
                                <CloseButton onPress={() => setIsInviteModalOpen(false)} />
                            </div>

                            {/* Tabs */}
                            <Tabs defaultSelectedKey="invite" className="flex-1">
                                <Tabs.List
                                    items={[
                                        { id: "invite", label: t('review.share.tabs.invite') },
                                        { id: "access", label: t('review.share.tabs.access') },
                                        { id: "link", label: t('review.share.tabs.link') },
                                    ]}
                                    type="underline"
                                    size="sm"
                                >
                                    {(item) => <Tabs.Item key={item.id}>{item.label}</Tabs.Item>}
                                </Tabs.List>

                                {/* Tab 1: Invita persone */}
                                <Tabs.Panel id="invite" className="mt-6">
                                    <div className="flex flex-col gap-6">
                                        {/* Radio per tipo invito */}
                                        <RadioGroup
                                            value={inviteType}
                                            onChange={(val: string) => setInviteType(val as "receiver" | "email" | "addressbook")}
                                        >
                                            <div className="flex gap-4">
                                                <RadioButton value="receiver" label={t('review.share.inviteType.receiver')} />
                                                <RadioButton value="email" label={t('review.share.inviteType.email')} />
                                                <RadioButton value="addressbook" label={t('review.share.inviteType.addressbook')} />
                                            </div>
                                        </RadioGroup>

                                        {/* Sezione in base al tipo */}
                                        {inviteType === "receiver" ? (
                                            <MultiSelect
                                                label={t('review.share.fields.recipients')}
                                                placeholder={t('review.share.fields.recipientsPlaceholder')}
                                                items={receiverItems}
                                                selectedItems={selectedReceivers}
                                                onItemInserted={() => { }}
                                                onItemCleared={() => { }}
                                            >
                                                {receiverItems.map((receiver) => (
                                                    <MultiSelect.Item key={receiver.id} {...receiver} />
                                                ))}
                                            </MultiSelect>
                                        ) : inviteType === "email" ? (
                                            <Input
                                                label={t('review.share.fields.email')}
                                                type="email"
                                                placeholder={t('review.share.fields.emailPlaceholder')}
                                                value={customEmail}
                                                onChange={(val) => setCustomEmail(val as string)}
                                            />
                                        ) : (
                                            <MultiSelect
                                                label={t('review.share.fields.contacts')}
                                                placeholder={t('review.share.fields.contactsPlaceholder')}
                                                items={addressBookItems}
                                                selectedItems={selectedAddressBookContacts}
                                                onItemInserted={() => { }}
                                                onItemCleared={() => { }}
                                            >
                                                {addressBookItems.map((contact) => (
                                                    <MultiSelect.Item key={contact.id} {...contact} />
                                                ))}
                                            </MultiSelect>
                                        )}

                                        {/* TextArea per note */}
                                        <TextArea
                                            label={t('review.share.fields.message')}
                                            placeholder={t('review.share.fields.messagePlaceholder')}
                                            value={inviteNotes}
                                            onChange={(val) => setInviteNotes(val as string)}
                                            rows={4}
                                        />

                                        {/* Footer */}
                                        <div className="flex items-center justify-end gap-3 pt-2">
                                            <Button color="secondary" onClick={() => setIsInviteModalOpen(false)}>
                                                {t('review.share.actions.cancel')}
                                            </Button>
                                            <Button
                                                color="primary"
                                                onClick={handleSendInvites}
                                                isDisabled={
                                                    inviteType === "receiver"
                                                        ? selectedReceivers.items.length === 0
                                                        : inviteType === "email"
                                                            ? !customEmail.trim()
                                                            : selectedAddressBookContacts.items.length === 0
                                                }
                                            >
                                                {t('review.share.actions.send')}
                                            </Button>
                                        </div>
                                    </div>
                                </Tabs.Panel>

                                {/* Tab 2: Persone con accesso */}
                                <Tabs.Panel id="access" className="mt-6">
                                    <div className="flex flex-col gap-4">
                                        {existingInvites.length === 0 ? (
                                            <div className="py-8 text-center text-tertiary">
                                                {t('review.share.noInvites', { ns: 'pages:documents' })}
                                            </div>
                                        ) : (
                                            existingInvites.map((invite) => {
                                                const displayName = getInviteDisplayName(invite);
                                                const displayEmail = getInviteDisplayEmail(invite);
                                                const account =
                                                    invite.type === "receiver" && invite.receiverId
                                                        ? accountService.getById(invite.receiverId)
                                                        : null;
                                                const addressBookEntry =
                                                    invite.type === "addressbook" && invite.addressBookEntryId
                                                        ? addressBookService.getById(invite.addressBookEntryId)
                                                        : null;

                                                return (
                                                    <div
                                                        key={invite.id}
                                                        className="flex items-center justify-between rounded-lg border border-secondary bg-primary p-4"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <Avatar
                                                                size="sm"
                                                                src={account?.email ? undefined : undefined}
                                                                initials={
                                                                    addressBookEntry
                                                                        ? `${addressBookEntry.firstName[0]}${addressBookEntry.lastName[0]}`.toUpperCase()
                                                                        : displayName.substring(0, 2).toUpperCase()
                                                                }
                                                            />
                                                            <div>
                                                                <div className="text-sm font-medium text-primary">{displayName}</div>
                                                                {displayEmail && (
                                                                    <div className="text-sm text-tertiary">{displayEmail}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <Badge type="pill-color" color={getStatusBadgeColor(invite.status)}>
                                                                {getStatusLabel(invite.status)}
                                                            </Badge>
                                                            {invite.status === "accepted" ? (
                                                                <Button
                                                                    color="secondary"
                                                                    size="sm"
                                                                    iconLeading={X}
                                                                    onClick={() => handleRevokeInvite(invite.id)}
                                                                >
                                                                    {t('review.share.revokeAccess', { ns: 'pages:documents' })}
                                                                </Button>
                                                            ) : (
                                                                <>
                                                                    <Button
                                                                        color="secondary"
                                                                        size="sm"
                                                                        iconLeading={Mail01}
                                                                        onClick={() => handleResendInvite(invite.id)}
                                                                    >
                                                                        {t('review.share.resend', { ns: 'pages:documents' })}
                                                                    </Button>
                                                                    <Button
                                                                        color="secondary"
                                                                        size="sm"
                                                                        iconLeading={X}
                                                                        onClick={() => handleRevokeInvite(invite.id)}
                                                                    >
                                                                        {t('review.share.revoke', { ns: 'pages:documents' })}
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </Tabs.Panel>

                                {/* Tab 3: Ottieni link */}
                                <Tabs.Panel id="link" className="mt-6">
                                    <div className="flex flex-col gap-6">
                                        {!shareLink ? (
                                            <>
                                                <div className="text-sm text-tertiary">
                                                    {t('review.share.generateLinkDescription')}
                                                </div>
                                                <div className="flex flex-col gap-4">
                                                    <div className="flex flex-col gap-2">
                                                        <label className="text-sm font-medium text-primary">{t('review.share.linkExpiration')}</label>
                                                        <select
                                                            value={linkExpiration}
                                                            onChange={(e) => setLinkExpiration(e.target.value)}
                                                            className="rounded-lg border border-secondary bg-primary px-3 py-2 text-sm dark:text-white"
                                                        >
                                                            <option value="never">{t('review.share.expirationOptions.never')}</option>
                                                            <option value="1day">{t('review.share.expirationOptions.1day')}</option>
                                                            <option value="7days">{t('review.share.expirationOptions.7days')}</option>
                                                            <option value="30days">{t('review.share.expirationOptions.30days')}</option>
                                                        </select>
                                                    </div>
                                                    <div className="flex flex-col gap-2">
                                                        <Checkbox
                                                            isSelected={linkPermissions.canView}
                                                            onChange={(val) =>
                                                                setLinkPermissions((prev) => ({ ...prev, canView: val }))}
                                                            label={t('review.share.permissions.view')}
                                                        />
                                                        <Checkbox
                                                            isSelected={linkPermissions.canEdit}
                                                            onChange={(val) =>
                                                                setLinkPermissions((prev) => ({ ...prev, canEdit: val }))}
                                                            label={t('review.share.permissions.edit')}
                                                        />
                                                    </div>
                                                </div>
                                                <Button color="primary" onClick={handleGenerateLink}>
                                                    {t('review.share.generateLink')}
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <div className="flex flex-col gap-2">
                                                    <label className="text-sm font-medium text-primary">{t('review.share.shareLink')}</label>
                                                    <div className="flex gap-2">
                                                        <Input value={shareLink} isDisabled className="flex-1" />
                                                        <Button
                                                            color="secondary"
                                                            iconLeading={Copy01}
                                                            onClick={handleCopyLink}
                                                        >
                                                            {copied ? t('review.share.copied') : t('review.share.copy')}
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="rounded-lg bg-warning-50 p-3 text-sm text-warning-700">
                                                    {t('review.share.linkWarning')}
                                                </div>
                                                <Button color="secondary" onClick={() => setShareLink(null)}>
                                                    {t('review.share.generateNewLink')}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </Tabs.Panel>
                            </Tabs>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
}


